import React from "react";

import { Calendar, FileText, MapPin, MessageSquare } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Separator } from "~/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "~/components/ui/sheet";
import { Textarea } from "~/components/ui/textarea";
import { useClientAddresses } from "~/hooks/useClientAddresses";
import AddressSelector from "~/pages/clients/components/client-detail/purchase-order/components/AddressSelector";
import OrderSummary from "~/pages/clients/components/client-detail/purchase-order/components/OrderSummary";
import ProductSelector from "~/pages/clients/components/client-detail/purchase-order/components/ProductSelector";
import { useQuotationForm } from "~/pages/presupuestos/hooks/useQuotationForm";
import { Client } from "~/types/client";
import { formatCurrency } from "~/utils/helpers";

interface CreatePresupuestoDrawerProps {
  client: Client;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (quotationId: string) => void;
}

const VALIDITY_OPTIONS = [
  { value: "7", label: "7 días" },
  { value: "15", label: "15 días" },
  { value: "30", label: "30 días" },
  { value: "60", label: "60 días" },
];

const CreatePresupuestoDrawer: React.FC<CreatePresupuestoDrawerProps> = ({
  client,
  open,
  onOpenChange,
  onSuccess,
}) => {
  const { addresses, isLoading: addressesLoading } = useClientAddresses(
    client.id.toString()
  );

  const {
    orderItems,
    total,
    editingItem,
    setEditingItem,
    handleAddProduct,
    handleEditItem,
    handleRemoveItem,
    selectedAddressId,
    handleAddressChange,
    comments,
    setComments,
    internalNotes,
    setInternalNotes,
    validityDays,
    setValidityDays,
    isFormValid,
    isSubmitting,
    handleSaveQuotation,
    resetForm,
  } = useQuotationForm({
    client,
    onSuccess: (quotationId) => {
      resetForm();
      onOpenChange(false);
      onSuccess?.(quotationId);
    },
  });

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  // Calculate IVA and total with IVA
  const iva = Math.round(total * 0.19);
  const totalWithIva = Math.round(total * 1.19);

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto sm:max-w-2xl"
      >
        <SheetHeader className="mb-4">
          <SheetTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Nueva Cotización
          </SheetTitle>
          <SheetDescription>
            Crear cotización para{" "}
            <span className="font-medium text-foreground">
              {client.name || client.company}
            </span>
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6">
          {/* Validity Period */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Calendar className="h-4 w-4" />
              Vigencia del Presupuesto
            </div>
            <Select
              value={String(validityDays)}
              onValueChange={(value) => setValidityDays(Number(value))}
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Seleccionar vigencia" />
              </SelectTrigger>
              <SelectContent>
                {VALIDITY_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Separator />

          {/* Address Selection */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <MapPin className="h-4 w-4" />
              Dirección de Entrega
              <span className="text-destructive">*</span>
            </div>
            <AddressSelector
              addresses={addresses}
              selectedAddressId={selectedAddressId}
              onSelect={handleAddressChange}
              clientId={String(client.id)}
              isLoading={addressesLoading}
            />
          </div>

          <Separator />

          {/* Product Selection */}
          <div className="space-y-2">
            <ProductSelector
              orderItems={orderItems}
              onAddProduct={handleAddProduct}
              editingItem={editingItem}
              onEditItem={handleEditItem}
              onCancelEdit={() => setEditingItem(null)}
            />
          </div>

          {/* Order Summary */}
          {orderItems.length > 0 && (
            <>
              <Separator />
              <OrderSummary
                items={orderItems}
                total={total}
                onEditItem={(item) => setEditingItem(item)}
                onRemoveItem={handleRemoveItem}
              />
            </>
          )}

          <Separator />

          {/* Comments */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <MessageSquare className="h-4 w-4" />
              Comentarios (Opcional)
            </div>
            <Textarea
              placeholder="Comentarios visibles para el cliente..."
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              rows={2}
            />
          </div>

          {/* Internal Notes */}
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">
              Notas Internas (no visibles para el cliente)
            </Label>
            <Textarea
              placeholder="Notas internas..."
              value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)}
              rows={2}
            />
          </div>

          <Separator />

          {/* Totals */}
          <div className="space-y-2 rounded-lg bg-muted/50 p-4">
            <div className="flex justify-between text-sm">
              <span>Subtotal Neto:</span>
              <span>{formatCurrency(total)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>IVA (19%):</span>
              <span>{formatCurrency(iva)}</span>
            </div>
            <Separator />
            <div className="flex justify-between text-lg font-semibold">
              <span>Total:</span>
              <span>{formatCurrency(totalWithIva)}</span>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              onClick={handleSaveQuotation}
              disabled={!isFormValid || isSubmitting}
            >
              {isSubmitting ? "Guardando..." : "Crear Presupuesto"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CreatePresupuestoDrawer;
